/**
 * SiteVicinityLayer.tsx — the micro-layer around a searched site (T-504 PR 2)
 *
 * Noreen asked to see block-level detail around a searched site. Ricardo ruled option (a) on
 * T-502: serve it from Postgres by a small bbox around the pin, no vector tiles. This is the
 * render half — GET /api/vicinity returns the blocks (migration 017) and block groups
 * (migration 014) near the site, and this layer draws them.
 *
 * IT IS SITE-DRIVEN, NOT VIEWPORT-DRIVEN. Every other overlay here refetches on moveend. This
 * one fires once per searched site and then sits still while the user pans and zooms around it.
 * That is the whole reason the ruling avoided tiles: a viewport-driven micro-layer over ~950k
 * national blocks is a tile pyramid with extra steps. It also means this layer does NOT touch
 * mapTiers' MICROTIER_MIN_ZOOM — that constant stays reserved and unwired.
 *
 * THE SEARCH MUST NOT GET SLOWER (Noreen's condition). The fetch here is deliberately separate
 * from, and later than, the site-score request: the score is what the user is waiting for, and
 * nothing on this path can delay it. The readout appears when it appears.
 *
 * Lifecycle mirrors RoadsLayer/CountyLayer (T-012 W2): sources + layers added ONCE and kept
 * mounted, a `visible` prop toggles layout visibility rather than tearing the source down, and
 * teardown is guarded by isMapAlive (BEAD-0037 — map.remove() on sign-out).
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'

export interface SiteVicinityLayerProps {
  /** The searched site (pin) the readout is drawn around. null → nothing fetched or shown. */
  site: { lat: number; lng: number } | null
  /** Show/hide the layer (driven by the LayersControl toggle). Default on when a site exists. */
  visible?: boolean
}

export const VICINITY_BLOCKS_SOURCE_ID = 'site-vicinity-blocks'
export const VICINITY_BGS_SOURCE_ID = 'site-vicinity-block-groups'
export const VICINITY_BLOCKS_FILL_LAYER_ID = 'site-vicinity-blocks-fill'
export const VICINITY_BLOCKS_LINE_LAYER_ID = 'site-vicinity-blocks-line'
export const VICINITY_BGS_FILL_LAYER_ID = 'site-vicinity-bgs-fill'
export const VICINITY_BGS_LINE_LAYER_ID = 'site-vicinity-bgs-line'

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** The shape GET /api/vicinity returns. */
interface VicinityResponse {
  center: { lat: number; lng: number }
  radiusM: number
  blocks: GeoJSON.FeatureCollection
  blockGroups: GeoJSON.FeatureCollection
}

async function fetchVicinity(
  lat: number,
  lng: number,
  signal: AbortSignal,
): Promise<VicinityResponse> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
  const r = await authedFetch(`/api/vicinity?${params}`, { signal })
  if (!r.ok) throw new Error(`vicinity ${r.status}`)
  return r.json() as Promise<VicinityResponse>
}

/** Format a [0,1] sub-score for the tooltip; '—' when the value is missing rather than '0.00'. */
function fmtUnit(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '—'
}

export function SiteVicinityLayer({ site, visible = true }: SiteVicinityLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)

  // One-time: add both sources, their layers (BGs first → underneath), and the block tooltip.
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true

    map.addSource(VICINITY_BGS_SOURCE_ID, { type: 'geojson', data: EMPTY })
    map.addSource(VICINITY_BLOCKS_SOURCE_ID, { type: 'geojson', data: EMPTY })

    // ── Block groups: context underneath. A BG CONTAINS its blocks, so it is drawn first and
    // kept faint — it is the backdrop for the block detail, not the subject.
    map.addLayer({
      id: VICINITY_BGS_FILL_LAYER_ID,
      type: 'fill',
      source: VICINITY_BGS_SOURCE_ID,
      paint: {
        // The two classifications migration 014 carries. Anything else (including the NULL
        // that means "scored, met neither threshold") stays neutral rather than borrowing a
        // colour that would assert a classification it does not have.
        'fill-color': [
          'match',
          ['get', 'bg_dominant_type'],
          'Residential_MFH',
          '#7c3aed',
          'Suburban_SFH',
          '#0ea5e9',
          '#94a3b8',
        ],
        'fill-opacity': 0.12,
      },
    })
    map.addLayer({
      id: VICINITY_BGS_LINE_LAYER_ID,
      type: 'line',
      source: VICINITY_BGS_SOURCE_ID,
      paint: { 'line-color': '#64748b', 'line-width': 1, 'line-opacity': 0.5 },
    })

    // ── Blocks: the subject. HUE by dominant type (the two classes the canonical export
    // carries), STRENGTH by CES — note CES_blk is on a 0-100 scale, NOT [0,1] like the other
    // sub-scores, so the interpolation stops are in those units.
    map.addLayer({
      id: VICINITY_BLOCKS_FILL_LAYER_ID,
      type: 'fill',
      source: VICINITY_BLOCKS_SOURCE_ID,
      paint: {
        'fill-color': [
          'match',
          ['get', 'block_dominant_type'],
          'Corridor',
          '#E67E22',
          'Retail/Leisure',
          '#16a34a',
          '#94a3b8',
        ],
        'fill-opacity': [
          'interpolate',
          ['linear'],
          ['coalesce', ['get', 'ces_blk'], 0],
          0,
          0.25,
          100,
          0.75,
        ],
      },
    })
    map.addLayer({
      id: VICINITY_BLOCKS_LINE_LAYER_ID,
      type: 'line',
      source: VICINITY_BLOCKS_SOURCE_ID,
      paint: { 'line-color': '#1f2937', 'line-width': 0.6, 'line-opacity': 0.6 },
    })

    const hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 8,
    })

    const handleEnter = (e: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const f = e.features?.[0]
      if (!f?.properties) return
      const p = f.properties as Record<string, unknown>
      const type = String(p['block_dominant_type'] ?? 'Block')
      const geoid = String(p['block_geoid'] ?? '')
      const ces = typeof p['ces_blk'] === 'number' ? (p['ces_blk'] as number).toFixed(1) : '—'
      hoverPopup
        .setLngLat(e.lngLat)
        .setHTML(
          `<div style="font-family:Inter,sans-serif;font-size:12px;padding:2px 0">
            <span style="font-weight:700;color:var(--foreground)">${type}</span><br/>
            <span style="color:var(--muted-foreground);font-size:10px">Block ${geoid}</span><br/>
            <span style="color:var(--muted-foreground);font-size:10px">CES ${ces} · CS ${fmtUnit(p['cs_blk'])} · RDS ${fmtUnit(p['rds_blk'])} · DTS ${fmtUnit(p['dts_blk'])}</span>
          </div>`,
        )
        .addTo(map)
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
      hoverPopup.remove()
    }

    map.on('mouseenter', VICINITY_BLOCKS_FILL_LAYER_ID, handleEnter)
    map.on('mouseleave', VICINITY_BLOCKS_FILL_LAYER_ID, handleLeave)

    return () => {
      initializedRef.current = false
      hoverPopup.remove()
      map.off('mouseenter', VICINITY_BLOCKS_FILL_LAYER_ID, handleEnter)
      map.off('mouseleave', VICINITY_BLOCKS_FILL_LAYER_ID, handleLeave)
      // BEAD-0037: skip teardown if MapView's map.remove() already ran (sign-out).
      if (isMapAlive(map)) {
        for (const id of [
          VICINITY_BLOCKS_LINE_LAYER_ID,
          VICINITY_BLOCKS_FILL_LAYER_ID,
          VICINITY_BGS_LINE_LAYER_ID,
          VICINITY_BGS_FILL_LAYER_ID,
        ]) {
          if (map.getLayer(id)) map.removeLayer(id)
        }
        for (const id of [VICINITY_BLOCKS_SOURCE_ID, VICINITY_BGS_SOURCE_ID]) {
          if (map.getSource(id)) map.removeSource(id)
        }
      }
    }
  }, [map])

  // Visibility toggle — show/hide without tearing the sources down.
  useEffect(() => {
    if (!map || !initializedRef.current) return
    const vis = visible ? 'visible' : 'none'
    for (const id of [
      VICINITY_BGS_FILL_LAYER_ID,
      VICINITY_BGS_LINE_LAYER_ID,
      VICINITY_BLOCKS_FILL_LAYER_ID,
      VICINITY_BLOCKS_LINE_LAYER_ID,
    ]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis)
    }
  }, [map, visible])

  // Fetch on SITE change (not viewport change). The AbortController is the staleness guard:
  // searching B while A is in flight aborts A, so a slow earlier response can never paint the
  // previous site's blocks around the new pin — the same out-of-order hazard applySiteScore
  // handles for the score itself, solved here by cancelling rather than by request-id compare.
  useEffect(() => {
    if (!map || !initializedRef.current) return

    const setBoth = (blocks: GeoJSON.FeatureCollection, bgs: GeoJSON.FeatureCollection) => {
      ;(map.getSource(VICINITY_BLOCKS_SOURCE_ID) as GeoJSONSource | undefined)?.setData(blocks)
      ;(map.getSource(VICINITY_BGS_SOURCE_ID) as GeoJSONSource | undefined)?.setData(bgs)
    }

    // No site (or hidden): clear, so a readout never outlives the pin it describes.
    if (!site || !visible) {
      setBoth(EMPTY, EMPTY)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    fetchVicinity(site.lat, site.lng, signal)
      .then((data) => {
        if (signal.aborted) return
        setBoth(data.blocks, data.blockGroups)
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== 'AbortError') console.error(err)
      })

    return () => controller.abort()
  }, [map, site, visible])

  return null
}
